import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserPlus,
  faSpinner,
  faExclamationCircle,
  faCheckCircle,
  faUserGraduate,
  faChalkboardTeacher,
  faUserShield,
  faClipboardCheck,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';

type RoleOption = 0 | 1 | 2 | 3; // 0 = Student, 1 = Instructor, 2 = Admin, 3 = Moderator

const roleOptions: { value: RoleOption; label: string; icon: any; description: string }[] = [
  { value: 0, label: 'Student', icon: faUserGraduate, description: 'Can enroll in courses and track progress' },
  { value: 1, label: 'Trainer', icon: faChalkboardTeacher, description: 'Trainer/Assessor: teaches, captures marks and issues statements of results' },
  { value: 3, label: 'Moderator', icon: faClipboardCheck, description: 'Read-only access to assigned courses for QCTO moderation' },
  { value: 2, label: 'Admin', icon: faUserShield, description: 'Full access to the admin panel' },
];

export default function AdminCreateUserPage() {
  const { accessToken } = useAuth();

  const [role, setRole] = useState<RoleOption>(1);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [specialization, setSpecialization] = useState('');

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const resetForm = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPassword('');
    setPhoneNumber('');
    setEmployeeId('');
    setSpecialization('');
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess(false);

    if (!firstName.trim() || !lastName.trim() || !email.trim() || !password.trim()) {
      setError('First name, last name, email, and password are all required.');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    try {
      setIsSaving(true);
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          email,
          firstName,
          lastName,
          password,
          role,
          phoneNumber: role === 0 ? phoneNumber : undefined,
          employeeId: role === 1 ? employeeId : undefined,
          specialization: role === 1 ? specialization : undefined,
        }),
      });

      const data = await response.json().catch(() => null);

      if (response.status === 401 || response.status === 403) {
        setError('Your session has expired or you do not have permission. Please sign in again as an administrator.');
        return;
      }

      if (!response.ok || !data?.success) {
        setError(data?.message || `Failed to create the account (server returned ${response.status}).`);
        return;
      }

      setSuccess(true);
      resetForm();
    } catch (err) {
      console.error(err);
      setError('An error occurred while creating the account.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <div className="bg-white border-b border-canvas-300 px-8 py-4 sticky top-0 z-10">
        <h1 className="text-2xl font-bold text-brand-950">Create User</h1>
        <p className="text-sm text-brand-900/60 mt-1">Create a new student, trainer, moderator, or admin account</p>
      </div>

      <div className="p-8 max-w-2xl">
        <div className="card-surface p-6 sm:p-8">
          {/* Role selector */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-brand-950 mb-3">Account type</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {roleOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setRole(opt.value)}
                  className={`text-left p-4 rounded-2xl border transition-all ${
                    role === opt.value
                      ? 'border-brand-600 bg-brand-50'
                      : 'border-canvas-300 hover:border-brand-300'
                  }`}
                >
                  <FontAwesomeIcon
                    icon={opt.icon}
                    className={`h-5 w-5 mb-2 ${role === opt.value ? 'text-brand-700' : 'text-brand-900/50'}`}
                  />
                  <p className="text-sm font-semibold text-brand-950">{opt.label}</p>
                  <p className="text-xs text-brand-900/50 mt-1">{opt.description}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-brand-950 mb-2">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-brand-950 mb-2">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-brand-950 mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@mokalenglms.co.za"
                className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-brand-950 mb-2">
                Temporary Password <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              <p className="text-xs text-brand-900/50 mt-1">Share this with the user so they can sign in and change it.</p>
            </div>

            {role === 0 && (
              <div>
                <label className="block text-sm font-semibold text-brand-950 mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
            )}

            {role === 1 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-brand-950 mb-2">Employee ID</label>
                  <input
                    type="text"
                    value={employeeId}
                    onChange={(e) => setEmployeeId(e.target.value)}
                    placeholder="Auto-generated if left blank"
                    className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-brand-950 mb-2">Specialization</label>
                  <input
                    type="text"
                    value={specialization}
                    onChange={(e) => setSpecialization(e.target.value)}
                    placeholder="e.g. Networking & Systems"
                    className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>
              </div>
            )}

            {role === 1 && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
                After creating this trainer account, assign them to a course from{' '}
                <span className="font-semibold">Admin → Trainers</span> so their dashboard has something to show.
              </div>
            )}

            {role === 3 && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
                After creating this moderator account, assign them to courses from{' '}
                <span className="font-semibold">Admin → Moderators</span>. They get read-only access to those courses.
              </div>
            )}

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                <FontAwesomeIcon icon={faExclamationCircle} className="h-4 w-4 text-red-700 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {success && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                <FontAwesomeIcon icon={faCheckCircle} className="h-4 w-4 text-green-700 flex-shrink-0" />
                <p className="text-sm text-green-700">Account created successfully.</p>
              </div>
            )}
          </div>

          <div className="flex justify-end mt-6 pt-6 border-t border-canvas-200">
            <button
              onClick={handleSubmit}
              disabled={isSaving}
              className="px-6 py-2 bg-brand-gradient text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 text-sm flex items-center gap-2"
            >
              <FontAwesomeIcon icon={isSaving ? faSpinner : faUserPlus} className={`h-3.5 w-3.5 ${isSaving ? 'animate-spin' : ''}`} />
              {isSaving ? 'Creating...' : 'Create Account'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
